import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  returnIsProduction,
  returnOrderStep,
  returnRedirectDuration,
  returnText
} from 'src/state/selectors';
import { MainComponent } from 'src/app/pages/main/main.component';
import {
  setDynamicError,
  setOrderState,
  setOrderStep
} from 'src/state/actions';
import {
  HelperFunctionsService,
  OrderState,
  ToastType
} from '../../services/helper-functions.service';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { SiToastService } from '@simpl/siemens-brand-ng/toast';
import { UserPrice } from 'src/models/user-price.model';
import { take } from 'rxjs';

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss']
})
export class OrderComponent implements OnInit {
  constructor(
    private store: Store,
    private main: MainComponent,
    private toastService: SiToastService,
    private helper: HelperFunctionsService
  ) {}

  // makes the OrderState enum available in the template
  OrderState = OrderState;
  orderStep = 1;
  order = false;
  time = {
    countDownLength: 1,
    count: 1
  };
  userPrices: UserPrice | null = null;
  returnIsProduction!: boolean;

  ngOnInit(): void {
    this.store.select(returnOrderStep).subscribe(val => {
      this.orderStep = val;
    });
    this.store.select(returnIsProduction).pipe(take(1)).subscribe(val => {
      this.returnIsProduction = val;
    });

    this.store
      .select(returnRedirectDuration('order'))
      .subscribe(val => {
        this.time.countDownLength = val;
      })
      .unsubscribe();

    // fetch the user prices from the environment variable
    // error handling, if the variable is not set or not valid JSON
    try {
      this.userPrices = JSON.parse(environment.NG_APP_USER_PRICES);
    } catch (error) {
      if (!this.returnIsProduction) {
        console.error('Error Parsing Prices: ', error);
      }
      this.helper.Toasts(
        this.toastService,
        ToastType.ERROR,
        'Order_priceFetchError'
      );
    }
  }

  ordering(): void {
    this.helper.extendPollingTimeOut(this.store);
    this.orderToken();
    this.store.dispatch(setOrderStep({ value: 2 }));
    this.store.dispatch(setOrderState({ value: OrderState.PROCESSING }));
    this.helper.countdown(this.time, this.redirect.bind(this));
  }

  // Followup action for the countdown timer
  redirect(): void {
    this.main.selectTab(0);
    this.order = !this.order;
    this.store.dispatch(setOrderStep({ value: 1 }));
  }

  // checks if the input is a valid number
  // only allows digits, commas and dots
  isNumbers(value: string): boolean {
    for (const char of value) {
      if (!/[\d.,]/.test(char)) {
        return false;
      }
    }
    return true;
  }

  /**
   * fetches a language specific message from the store
   * @param id
   * @returns msg
   */
  textFetch(id: string): string {
    let msg = '';
    this.store
      .select(returnText(id))
      .subscribe(val => {
        msg = val;
      })
      .unsubscribe();
    return msg;
  }

  /**
   * POST request to the backend for a new token
   * Sets the token in store
   */
  async orderToken(): Promise<void> {
    await axios({
      method: 'post',
      url: `${environment.NG_APP_BACKEND}/token`,
      withCredentials: true,
      data: 'dummy-needed-by-tomcat-cors-filter',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
      .then(() => {
        // handle success
        this.helper.Toasts(
          this.toastService,
          ToastType.SUCCESS,
          'Order_processing'
        );
      })
      .catch(error => {
        // Log the full error object for debugging
        console.log(error);
        console.log(error.response);
        console.log(error.response.data);
        console.log(error.response.data.message);

        // Log the backend error response if available
        this.store.dispatch(
          setDynamicError({ value: this.helper.extractErrorMessage(error) })
        );

        if (error.response?.status === 400) {
          this.helper.Toasts(this.toastService, ToastType.ERROR, 'IDP_Error');
        }
        this.helper.Toasts(this.toastService, ToastType.ERROR, 'Dynamic_Error');
        if (!this.returnIsProduction || error.toJSON) {
          console.error('Token Order error: ' + JSON.stringify(error.toJSON()));
        } else {
          console.error('Token Order error: ', JSON.stringify(error));
        }
      });
  }
}
